import {useCallback, useMemo} from 'react';
import {Connection} from 'reactflow';

import {
  canDrawOutgoingEdgeFromStatus,
  type ConnectionRole,
  isUpstreamToActiveConnection,
} from './procedureRunDesignFlowchartConnectionUtils';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {UnitOperationStatusType} from './useMultiProcedureRunStudyProgress';
import type {ProcedureRunDesignFlowchartLayoutEdge} from './useProcedureRunDesignFlowchartEdges';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartConnectionHandlersArgs {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  onDrawEdge: (spec: FlowchartEdgeSpec) => void;
  activeMethodId: string | null;
  isEditable: boolean;
}

export interface UseProcedureRunDesignFlowchartConnectionHandlersResult {
  nodesConnectable: boolean;
  isValidConnection: (connection: Connection) => boolean;
  onConnect: (connection: Connection) => void;
}

/**
 * Wires the drag-to-connect interaction for the flowchart: whether the canvas is connectable, how a
 * drag is validated, and what a completed drag does (reports the new edge up to the draft edge state).
 *
 * Validation is a composition of pure predicates from `procedureRunDesignFlowchartConnectionUtils`, so
 * future rules (e.g. node-status gating) can be added by ANDing in another predicate here.
 */
export function useProcedureRunDesignFlowchartConnectionHandlers({
  nodes,
  edges,
  onDrawEdge,
  activeMethodId,
  isEditable,
}: UseProcedureRunDesignFlowchartConnectionHandlersArgs): UseProcedureRunDesignFlowchartConnectionHandlersResult {
  // Drawing is allowed only with an active layer in edit mode, matching where the affordances appear.
  const nodesConnectable = activeMethodId != null && isEditable;

  // Look up a grid node's connection role by id so the validator stays in lockstep with the derived
  // roles on the nodes (read from node data, never parsed out of ids).
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const getConnectionRole = useCallback(
    (nodeId: string): ConnectionRole => {
      const node = nodesById.get(nodeId);
      return node != null && isProcedureRunDesignFlowchartNode(node) ? node.data.connectionRole : null;
    },
    [nodesById]
  );
  const getStatus = useCallback(
    (nodeId: string): UnitOperationStatusType | undefined => {
      const node = nodesById.get(nodeId);
      return node != null && isProcedureRunDesignFlowchartNode(node) ? node.data.status : undefined;
    },
    [nodesById]
  );

  // Validation rejects anything but a brand-new upstream->active edge. These never fire unless a drag
  // starts, which `nodesConnectable` gates (no connectable handles render otherwise), so they can be
  // wired unconditionally.
  const isValidConnection = useCallback(
    (connection: Connection) =>
      connection.source != null &&
      canDrawOutgoingEdgeFromStatus(getStatus(connection.source)) &&
      isUpstreamToActiveConnection(connection, edges, getConnectionRole),
    [edges, getConnectionRole, getStatus]
  );
  // Reports the new edge to the owner of the draft edge state; rendering and the live source-handle
  // recompute follow from the edge flowing back down.
  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source == null || connection.target == null) {
        return;
      }
      onDrawEdge({sourceNodeId: connection.source, targetNodeId: connection.target});
    },
    [onDrawEdge]
  );

  return {nodesConnectable, isValidConnection, onConnect};
}
